import * as React from 'react';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ConfigProvider } from 'antd';
import './globals.scss';
import './scss/override.antds.scss';
import { QueryClient, QueryClientProvider } from 'react-query';
import moment from 'moment';
import RenderRoutes, { routes } from './routes/routes';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'types/RootState';
import { getCurrentUser, setAuth, updateFcmTokenUser } from 'store/auth/authSlice';
import Toast from './common/UIParts/Toast';
import {
  getRefreshTokenFromCookie,
  getTokenFromCookie,
  getLanguage,
  saveLanguage,
} from 'helpers/cookies';
import { getUserFromLocalStorage } from 'helpers/localStorage';
import { generateToken, messaging } from '../services/firebase';
import { onMessage } from 'firebase/messaging';
import SpinnerLoading from './components/Loading/Loading';

// Set language + moment
const language = getLanguage();
saveLanguage(language);
moment.locale(language);

const ConfigProviderCustom = ConfigProvider as any;

export function App() {
  const { i18n } = useTranslation();
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: true,
        refetchOnReconnect: false,
        staleTime: 10 * 60 * 1000,
      },
    },
  });

  const dispatch = useDispatch();
  const { authenticated, currentUser } = useSelector((state: RootState) => state.auth);
  const { loadingApp } = useSelector((state: RootState) => state.toast);
  const localUser = getUserFromLocalStorage();
  const localToken = getTokenFromCookie();
  const localRefreshToken = getRefreshTokenFromCookie();
  const checkAuthLocal = !!(localToken && localRefreshToken && localUser);

  useEffect(() => {
    if (checkAuthLocal) {
      dispatch(setAuth());
    }
  }, [checkAuthLocal]);

  useEffect(() => {
    authenticated && checkAuthLocal && dispatch(getCurrentUser());
  }, [authenticated, checkAuthLocal]);

  const updateFcmToken = async ({ requestPermission = false } = {}) => {
    const token = await generateToken({ requestPermission });
    if (token && (currentUser.com || currentUser.consultant)) {
      dispatch(
        updateFcmTokenUser({
          fcmToken: token,
          email: currentUser.com?.email ?? currentUser.consultant?.email,
        }),
      );
    }
  };

  useEffect(() => {
    Object.keys(currentUser).length > 0 && updateFcmToken();
  }, [currentUser]);

  useEffect(() => {
    if (!messaging || Object.keys(currentUser).length === 0) return;
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'default') return;

    const requestTokenByUserAction = () => {
      updateFcmToken({ requestPermission: true });
    };

    // Chromium blocks permission prompts without user interaction.
    window.addEventListener('pointerdown', requestTokenByUserAction, { once: true });

    return () => {
      window.removeEventListener('pointerdown', requestTokenByUserAction);
    };
  }, [currentUser]);

  useEffect(() => {
    if (messaging) {
      onMessage(messaging, async (payload) => {
        if (!('Notification' in window)) {
          return;
        }
        if (Notification.permission !== 'granted') {
          return;
        }
        console.log('[FCM][foreground] payload:', payload);

        const title = payload.notification?.title ?? payload.data?.title ?? 'New message';
        const options = {
          body: payload.notification?.body ?? payload.data?.body,
          icon: payload.notification?.icon ?? payload.data?.icon ?? '/favicon.png',
          image: payload.notification?.image ?? payload.data?.image,
          requireInteraction: true,
          renotify: true,
          tag: payload.data?.tag ?? `chat-${payload.messageId ?? Date.now()}`,
        };
        try {
          // Prefer SW notification for consistent behavior across browsers.
          console.log('options', options);
          if ('serviceWorker' in navigator) {
            const registration = await navigator.serviceWorker.getRegistration(
              '/firebase-messaging-sw.js',
            );
            if (registration) {
              await registration.showNotification(title, options);
              return;
            }
          }
          new Notification(title, options);
        } catch (error) {
          console.error('[FCM][foreground] failed to show notification:', error);
        }
      });
    }
  }, []);

  const isAuthenticated = !!(authenticated && currentUser && Object.keys(currentUser).length);

  return (
    <ConfigProviderCustom
      theme={{
        token: {
          colorPrimary: '#ff4218',
        },
      }}
    >
      <BrowserRouter>
        <Helmet
          titleTemplate="%s - きゃりこん"
          defaultTitle="きゃりこん"
          htmlAttributes={{ lang: i18n.language }}
        >
          <meta name="description" content="きゃりこん (.com)" />
        </Helmet>

        <QueryClientProvider client={queryClient}>
          <Toast />
          <SpinnerLoading loading={!!loadingApp}>
            <RenderRoutes
              routes={routes}
              isAuthenticated={isAuthenticated || checkAuthLocal}
              currentUser={localUser}
            />
          </SpinnerLoading>
        </QueryClientProvider>
      </BrowserRouter>
    </ConfigProviderCustom>
  );
}
